import React, { useState, useEffect } from 'react';
import { ClipboardList, Plus, Upload, Sparkles, Loader2, AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { regulatoryAPI, exportAPI } from '../../services/api';
import ExportMenu from '../../components/ExportMenu';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const STATUS = {
  pending:      { cls:'bg-amber-100 text-amber-700',   label:'Pending',      icon: Clock        },
  in_progress:  { cls:'bg-blue-100 text-blue-700',     label:'In Progress',  icon: Clock        },
  submitted:    { cls:'bg-emerald-100 text-emerald-700',label:'Submitted',    icon: CheckCircle  },
  overdue:      { cls:'bg-red-100 text-red-700',        label:'Overdue',      icon: AlertTriangle},
  not_required: { cls:'bg-gray-100 text-gray-500',      label:'Not Required', icon: XCircle      },
};

const JURISDICTIONS = ['DE','NL','ES','EU','AT','FR','IT','PL','other'];

const F = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>;
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;
const S = ({ children, ...p }) => <select {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">{children}</select>;

export default function RegulatoryPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('super_admin','compliance');

  const [filings, setFilings]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [statusFilter, setStatus] = useState('');
  const [jurisFilter, setJuris]   = useState('');
  const [createModal, setCreate]  = useState(false);
  const [saving, setSaving]       = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [uploading, setUploading] = useState(false);

  const initForm = { title:'', jurisdiction:'DE', filing_type:'', deadline:'', status:'pending', responsible_id:'', notes:'' };
  const [form, setForm] = useState(initForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await regulatoryAPI.list({ status: statusFilter||undefined, jurisdiction: jurisFilter||undefined });
      setFilings(data.data.items || data.data);
      setTotal(data.data.pagination?.total || (data.data.items||data.data).length);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, jurisFilter]);

  const loadDetail = async (id) => {
    try { const { data } = await regulatoryAPI.get(id); setSelected(data.data); }
    catch {}
  };

  const save = async () => {
    setSaving(true);
    try { await regulatoryAPI.create(form); toast.success('Filing created'); setCreate(false); setForm(initForm); load(); }
    catch {} finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try { await regulatoryAPI.update(id, { status }); toast.success('Status updated'); load(); if (selected?.id===id) setSelected(s=>({...s, status})); }
    catch {}
  };

  const prefill = async (id) => {
    setPrefilling(true);
    try {
      const { data } = await regulatoryAPI.prefill(id);
      setSelected(s=>({...s, ai_prefill_data: data.data.draft}));
      toast.success('AI pre-fill complete');
    } catch {} finally { setPrefilling(false); }
  };

  const uploadDoc = async (e, id) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await regulatoryAPI.uploadDoc(id, fd);
      toast.success('Document uploaded');
      loadDetail(id);
    } catch {} finally { setUploading(false); e.target.value=''; }
  };

  // Stats
  const overdue   = filings.filter(f=>f.status==='overdue').length;
  const upcoming7 = filings.filter(f=>f.days_remaining>=0&&f.days_remaining<=7&&f.status!=='submitted').length;
  const upcoming30 = filings.filter(f=>f.days_remaining>=0&&f.days_remaining<=30&&f.status!=='submitted').length;

  const set = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Regulatory Filing</h1>
          <p className="text-sm text-gray-500">{total} filings tracked</p>
        </div>
        {canWrite && <button onClick={()=>setCreate(true)} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={14}/>Add Filing</button>}
          <ExportMenu exports={[
            { label:'Excel', apiFn: exportAPI.regulatoryExcel, filename:'regulatory-filings.xlsx' },
            { label:'PDF',   apiFn: exportAPI.regulatoryPDF,   filename:'regulatory-filings.pdf'  },
          ]}/>
      </div>

      {/* Alerts */}
      {(overdue>0||upcoming7>0) && (
        <div className="space-y-2">
          {overdue>0 && <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
            <AlertTriangle size={16} className="text-danger shrink-0"/>
            <p className="text-sm text-red-800 font-medium">{overdue} filing{overdue>1?'s':''} overdue</p>
          </div>}
          {upcoming7>0 && <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
            <Clock size={16} className="text-amber-600 shrink-0"/>
            <p className="text-sm text-amber-800 font-medium">{upcoming7} filing{upcoming7>1?'s':''} due within 7 days</p>
          </div>}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['Overdue', overdue, 'bg-red-50'],['Due 7 days', upcoming7,'bg-amber-50'],['Due 30 days',upcoming30,'bg-yellow-50'],
          ['Submitted',filings.filter(f=>f.status==='submitted').length,'bg-emerald-50']].map(([l,v,bg])=>(
          <div key={l} className={clsx('rounded-xl border border-gray-200 p-4',bg)}>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{l}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{v}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3">
        <div className="flex gap-1 flex-wrap">
          {['','pending','in_progress','submitted','overdue'].map(s=>(
            <button key={s} onClick={()=>setStatus(s)}
              className={clsx('px-3 py-1.5 text-xs rounded-lg font-medium capitalize transition-colors',
                statusFilter===s?'bg-primary-500 text-white':'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100')}>
              {s?STATUS[s]?.label:'All'}
            </button>
          ))}
        </div>
        <select value={jurisFilter} onChange={e=>setJuris(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white ml-auto focus:outline-none">
          <option value="">All Jurisdictions</option>
          {JURISDICTIONS.map(j=><option key={j} value={j}>{j}</option>)}
        </select>
      </div>

      {/* Table + detail */}
      <div className={clsx('grid gap-4', selected?'grid-cols-1 lg:grid-cols-2':'grid-cols-1')}>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>{['Filing','Jurisdiction','Deadline','Days','Status',''].map(h=><th key={h} className={clsx('px-4 py-3 font-medium',h===''?'text-right':'text-left')}>{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading?<tr><td colSpan={6} className="text-center py-12"><Loader2 size={20} className="animate-spin text-gray-400 mx-auto"/></td></tr>
                :filings.length===0?<tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">No filings found.</td></tr>
                :filings.map((f,i)=>{ const Icon=STATUS[f.status]?.icon||Clock; return (
                  <tr key={f.id} onClick={()=>loadDetail(f.id)}
                    className={clsx('hover:bg-hover cursor-pointer transition-colors',i%2===0?'bg-white':'bg-surface',selected?.id===f.id&&'ring-2 ring-inset ring-primary-500/30')}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{f.title}</p>
                      <p className="text-xs text-gray-400">{f.filing_type||'General filing'}</p>
                    </td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono font-bold">{f.jurisdiction}</span></td>
                    <td className="px-4 py-3 text-gray-600">{new Date(f.deadline).toLocaleDateString('en-GB')}</td>
                    <td className="px-4 py-3">
                      {f.days_remaining!==null&&<span className={clsx('text-xs font-semibold',f.days_remaining<0?'text-danger':f.days_remaining<=7?'text-danger':f.days_remaining<=30?'text-warn':'text-gray-400')}>
                        {f.days_remaining<0?`${Math.abs(f.days_remaining)}d overdue`:`${f.days_remaining}d`}
                      </span>}
                    </td>
                    <td className="px-4 py-3"><span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',STATUS[f.status]?.cls)}><Icon size={10}/>{STATUS[f.status]?.label}</span></td>
                    <td className="px-4 py-3 text-right" onClick={e=>e.stopPropagation()}>
                      {canWrite && f.status!=='submitted' && (
                        <button onClick={()=>updateStatus(f.id,'submitted')} className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">Mark Submitted</button>
                      )}
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail */}
        {selected && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div><h3 className="font-semibold text-gray-900">{selected.title}</h3><p className="text-xs text-gray-400">{selected.jurisdiction} · {selected.filing_type||'General'}</p></div>
              <button onClick={()=>setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            {canWrite && <button onClick={()=>prefill(selected.id)} disabled={prefilling}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 disabled:opacity-60">
              {prefilling?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>}
              {prefilling?'AI pre-filling…':'AI Pre-fill from System Data'}
            </button>}

            {selected.ai_prefill_data && (
              <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 text-xs text-gray-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{selected.ai_prefill_data}</div>
            )}

            <div className="space-y-1.5 text-sm">
              {[['Deadline',new Date(selected.deadline).toLocaleDateString('en-GB')],
                ['Submission Date',selected.submission_date?new Date(selected.submission_date).toLocaleDateString('en-GB'):'Not submitted'],
                ['Reference',selected.reference_number||'—'],
              ].map(([l,v])=>(
                <div key={l} className="flex justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-gray-500">{l}</span><span className="font-medium text-gray-900">{v}</span>
                </div>
              ))}
            </div>

            {/* Documents */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Documents ({selected.documents?.length||0})</p>
                {canWrite && <label className={clsx('flex items-center gap-1 text-xs text-primary-600 cursor-pointer hover:underline',uploading&&'opacity-60')}>
                  {uploading?<Loader2 size={11} className="animate-spin"/>:<Upload size={11}/>} Upload
                  <input type="file" className="hidden" onChange={e=>uploadDoc(e,selected.id)}/>
                </label>}
              </div>
              <div className="space-y-1.5">
                {(selected.documents||[]).map(d=>(
                  <div key={d.id} className="flex items-center justify-between py-1.5 text-xs">
                    <span className="text-gray-700 truncate">{d.file_name}</span>
                    <a href={d.file_path} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline ml-2">Download</a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-base font-semibold text-gray-900">New Regulatory Filing</h2>
              <button onClick={()=>setCreate(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <F label="Title *"><I value={form.title} onChange={set('title')} placeholder="e.g. Annual EEG Report 2024"/></F>
              <div className="grid grid-cols-2 gap-4">
                <F label="Jurisdiction"><S value={form.jurisdiction} onChange={set('jurisdiction')}>
                  {JURISDICTIONS.map(j=><option key={j} value={j}>{j}</option>)}
                </S></F>
                <F label="Filing Type"><I value={form.filing_type} onChange={set('filing_type')} placeholder="e.g. EEG, BNetzA"/></F>
                <F label="Deadline *"><I type="date" value={form.deadline} onChange={set('deadline')}/></F>
                <F label="Status"><S value={form.status} onChange={set('status')}>
                  {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </S></F>
              </div>
              <F label="Notes"><textarea value={form.notes} onChange={set('notes')} rows={3} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/30"/></F>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
              <button onClick={()=>setCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
                {saving&&<Loader2 size={14} className="animate-spin"/>} Create Filing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
